import type React from "react";
import { useMemo, useState } from "react";
import * as THREE from "three";
import { Html, Line } from "@react-three/drei";
import type { Fixation, Saccade } from "../../types/gazeHeatmapTypes";
import FixationTooltip from "./FixationTooltip";

interface ScanpathOverlay3DProps {
  /** Array of fixations to render */
  fixations: Fixation[];
  /** Array of saccades to render */
  saccades: Saccade[];
  /** Current fixation (highlighted) */
  currentFixation?: Fixation | null;
  /** Sphere radius (should match the 360 sphere) */
  sphereRadius?: number;
  /** Color for fixation points */
  fixationColor?: string;
  /** Color for saccade lines */
  saccadeColor?: string;
  /** Color for current fixation highlight */
  currentFixationColor?: string;
  /** Base size for fixation spheres */
  fixationBaseSize?: number;
  /** Whether to show fixation numbers */
  showNumbers?: boolean;
  /** Opacity (0-1) */
  opacity?: number;
  /** Scene rotation Y applied to the 360 sphere (to match coordinates) */
  sceneRotationY?: number;
  /** Horizontal U offset (0-1) for video alignment */
  uOffset?: number;
}

/**
 * Convert UV coordinates to 3D position on a sphere
 * The sphere is viewed from inside, so we need to position elements
 * just inside the sphere surface
 *
 * This formula matches Three.js SphereGeometry's UV mapping:
 *   x = -r * cos(u * 2π) * sin(v * π)
 *   y = r * cos(v * π)
 *   z = r * sin(u * 2π) * sin(v * π)
 *
 * The shader does correctedUv = (1-vUv.x, vUv.y) for BackSide rendering,
 * so a heatmap point at UV (u, v) appears at sphere UV (1-u, v).
 * We need to place the scanpath marker at that same 3D position.
 */
function uvToSpherePosition(u: number, v: number, radius: number, uOffset = 0): THREE.Vector3 {
  let adjustedU = u + uOffset;
  if (adjustedU > 1) adjustedU -= 1;
  if (adjustedU < 0) adjustedU += 1;

  const sphereU = 1.0 - adjustedU;
  const sphereV = 1.0 - v;

  const theta = sphereU * 2 * Math.PI;
  const phi = sphereV * Math.PI;

  const innerRadius = radius * 0.98;

  const x = -innerRadius * Math.cos(theta) * Math.sin(phi);
  const y = innerRadius * Math.cos(phi);
  const z = innerRadius * Math.sin(theta) * Math.sin(phi);

  return new THREE.Vector3(x, y, z);
}

/**
 * 3D overlay for rendering scanpath on the inside of the 360° sphere
 * Renders fixations as spheres and saccades as lines
 */
const ScanpathOverlay3D: React.FC<ScanpathOverlay3DProps> = ({
  fixations,
  saccades,
  currentFixation,
  sphereRadius = 500,
  fixationColor = "#3b82f6",
  saccadeColor = "#ffffff",
  currentFixationColor = "#ef4444",
  fixationBaseSize = 44,
  showNumbers = true,
  opacity = 1,
  sceneRotationY: _sceneRotationY = 0,
  uOffset = 0,
}) => {
  const [hoveredFixationId, setHoveredFixationId] = useState<number | null>(null);

  const saccadeLineData = useMemo(() => {
    if (saccades.length === 0) return [];

    const lines: Array<[THREE.Vector3, THREE.Vector3]> = [];

    for (const saccade of saccades) {
      const from = fixations.find((f) => f.id === saccade.fromFixationId);
      const to = fixations.find((f) => f.id === saccade.toFixationId);

      if (!from || !to) continue;

      const startPos = uvToSpherePosition(
        from.centroidUV.u,
        from.centroidUV.v,
        sphereRadius,
        uOffset,
      );
      const endPos = uvToSpherePosition(to.centroidUV.u, to.centroidUV.v, sphereRadius, uOffset);

      lines.push([startPos, endPos]);
    }

    return lines;
  }, [saccades, fixations, sphereRadius, uOffset]);

  return (
    <group>
      {saccadeLineData.map((points, index) => (
        <Line
          key={index}
          points={points}
          color={saccadeColor}
          lineWidth={3}
          opacity={opacity * 0.9}
          transparent={true}
        />
      ))}

      {fixations.map((fixation) => {
        const position = uvToSpherePosition(
          fixation.centroidUV.u,
          fixation.centroidUV.v,
          sphereRadius,
          uOffset,
        );

        const isCurrent = currentFixation?.id === fixation.id;
        const isHovered = hoveredFixationId === fixation.id;
        const color = isCurrent ? currentFixationColor : fixationColor;

        const durationScale = Math.sqrt(fixation.duration / 100);
        const sizeMultiplier = Math.min(2, Math.max(0.75, durationScale));
        const pixelSize = Math.round(fixationBaseSize * sizeMultiplier);

        return (
          // eslint-disable-next-line react/no-unknown-property -- react-three-fiber intrinsic prop (position), not a DOM attribute
          <group key={fixation.id} position={position}>
            <Html
              center
              occlude={false}
              zIndexRange={[40, 0]}
              style={{
                pointerEvents: "auto",
                userSelect: "none",
                opacity: opacity,
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: `${pixelSize}px`,
                  height: `${pixelSize}px`,
                  cursor: "pointer",
                }}
                onMouseEnter={() => setHoveredFixationId(fixation.id)}
                onMouseLeave={() => setHoveredFixationId(null)}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    backgroundColor: `${color}cc`,
                    border: `3px solid ${color}`,
                    boxShadow:
                      isCurrent || isHovered
                        ? `0 0 0 4px ${
                            isCurrent ? currentFixationColor : fixationColor
                          }40, 0 2px 8px rgba(0,0,0,0.5)`
                        : "0 2px 8px rgba(0,0,0,0.5)",
                    transform: isHovered ? "scale(1.1)" : "scale(1)",
                    transition: "transform 0.15s ease-out",
                  }}
                />
                {showNumbers && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: `${Math.max(12, Math.round(pixelSize * 0.5))}px`,
                      fontWeight: "bold",
                      textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                    }}
                  >
                    {fixation.id + 1}
                  </div>
                )}

                {isHovered && <FixationTooltip fixation={fixation} />}
              </div>
            </Html>
          </group>
        );
      })}
    </group>
  );
};

export default ScanpathOverlay3D;
